/**
 * InvoiceUpload Component
 * A drag-and-drop file upload component for invoice processing with AI
 */

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { isAxiosError } from 'axios';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { Icon } from '../ui/Icon';
import { useInvoiceOperations } from '../../hooks/useApi';
import { type OcrTaskStartResponse } from '@defs/api';
import {
  useTaskProcessingStore,
  createProcessingTaskFromStartResponse,
} from '../../stores/taskProcessing.store';
import type { ProcessingTask } from '../../stores/taskProcessing.types';
import { TaskStatusList } from '../../features/tasks';
import toast from 'react-hot-toast';

// ============================================================================
// Types & Constants
// ============================================================================

interface FileWithPreview extends File {
  preview: string | undefined;
}

const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);

function getUploadErrorMessage(error: unknown): string {
  if (
    isAxiosError<{ detail?: unknown }>(error) &&
    typeof error.response?.data?.detail === 'string'
  ) {
    return error.response.data.detail;
  }
  return error instanceof Error
    ? error.message
    : 'Error al procesar la factura. Intente nuevamente.';
}

// ============================================================================
// Styled Components
// ============================================================================

const UploadContainer = styled.div`
  width: 100%;
`;

const UploadTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
`;

const DropZone = styled.div<{
  $isDragActive: boolean;
  $hasError: boolean;
  $isDragReject: boolean;
}>`
  border: 1px dashed;
  border-radius: var(--radius-lg);
  padding: 2rem 1.25rem;
  text-align: center;
  background: var(--surface-strong);
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-bottom: var(--space-lg);

  /* Border colors */
  ${({ $isDragActive, $hasError, $isDragReject }) => {
    if ($hasError || $isDragReject) {
      return css`
        border-color: var(--danger-600);
        background: var(--danger-50);
      `;
    }
    if ($isDragActive) {
      return css`
        border-color: var(--success-600);
        background: var(--success-50);
      `;
    }
    return css`
      border-color: var(--border);
      background: var(--surface-strong);
    `;
  }}

  &:hover:not([data-disabled='true']) {
    border-color: var(--brand-600);
    background: var(--brand-50);

    box-shadow: none;
  }

  /* Disabled state */
  &[data-disabled='true'] {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 0.75rem;
  color: var(--text-muted);
  svg {
    width: 28px;
    height: 28px;
  }
`;

const UploadText = styled.div`
  h3 {
    margin: 0 0 var(--space-sm) 0;
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
  }

  p {
    margin: 0 0 var(--space-md) 0;
    color: var(--text-muted);
    font-size: var(--font-size-base);
    line-height: var(--line-height-relaxed);
  }

  .file-requirements {
    font-size: var(--font-size-sm);
    color: var(--text-muted);
    font-style: italic;
  }
`;

const FilePreviewContainer = styled.div`
  background: var(--neutral-25);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-lg);
  display: flex;
  align-items: center;
  gap: var(--space-lg);
`;

const FileIcon = styled.div`
  font-size: 3rem;
  color: var(--text-muted);
`;

const FileInfo = styled.div`
  flex: 1;

  .file-name {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-xs);
  }

  .file-size {
    font-size: var(--font-size-sm);
    color: var(--text-muted);
    margin-bottom: var(--space-xs);
  }

  .file-type {
    font-size: var(--font-size-sm);
    color: var(--brand-600);
    font-weight: var(--font-weight-medium);
  }
`;

const RemoveButton = styled(Button)`
  flex-shrink: 0;
`;

const UploadActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  align-items: center;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: var(--danger-600);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-sm);
  text-align: center;
`;

const StatusList = styled(TaskStatusList)`
  margin-top: var(--space-xl);
`;

// ============================================================================
// Component
// ============================================================================

export const InvoiceUpload: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scanInvoice, isLoading: isMutationLoading } = useInvoiceOperations();
  const startTask = useTaskProcessingStore((state) => state.startTask);
  const invoiceTaskMatch = useCallback(
    (task: ProcessingTask) =>
      task.type === 'ocr' && task.subtype?.startsWith('invoice'),
    [],
  );
  const [currentFile, setCurrentFile] = useState<FileWithPreview | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Task started by this upload; used to auto-navigate to the results page
  // once processing finishes (the manual task-card CTA remains as a fallback).
  const [pendingResultTaskId, setPendingResultTaskId] = useState<string | null>(
    null,
  );
  const hasNavigatedRef = useRef(false);
  const isMountedRef = useRef(true);
  const latestPathRef = useRef(location.pathname);

  const trackedTask = useTaskProcessingStore((state) =>
    pendingResultTaskId ? state.tasks[pendingResultTaskId] : undefined,
  );

  useEffect(() => {
    if (!trackedTask || hasNavigatedRef.current) {
      return;
    }

    if (
      trackedTask.normalizedStatus === 'success' &&
      trackedTask.resultId != null
    ) {
      hasNavigatedRef.current = true;
      setPendingResultTaskId(null);
      navigate(`/invoice-results?id=${trackedTask.resultId}&type=ocr`);
      return;
    }

    // Stop tracking on a terminal non-success state; the task card surfaces
    // the error and any retry affordances.
    if (
      trackedTask.normalizedStatus === 'failure' ||
      trackedTask.normalizedStatus === 'canceled'
    ) {
      setPendingResultTaskId(null);
    }
  }, [trackedTask, navigate]);

  useEffect(() => {
    latestPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      return 'Tipo de archivo no válido. Solo se aceptan JPG, PNG y PDF.';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'El archivo es demasiado grande. El tamaño máximo es 5MB.';
    }

    return null;
  }, []);

  // File drop handler
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (!rejection) {
          setError('Error al procesar el archivo.');
          return;
        }
        const error = rejection.errors[0];
        if (!error) {
          setError('Error al procesar el archivo.');
          return;
        }

        if (error.code === 'file-too-large') {
          setError('El archivo es demasiado grande. El tamaño máximo es 5MB.');
        } else if (error.code === 'file-invalid-type') {
          setError(
            'Tipo de archivo no válido. Solo se aceptan JPG, PNG y PDF.',
          );
        } else {
          setError('Error al procesar el archivo.');
        }
        return;
      }

      // Handle accepted files
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Type guard to ensure file exists
        if (!file || !(file instanceof File)) {
          setError('Error: No se pudo obtener el archivo.');
          return;
        }

        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        // Create preview for images
        const fileWithPreview = Object.assign(file, {
          preview: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : undefined,
        });

        setCurrentFile(fileWithPreview);
      }
    },
    [validateFile],
  );

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: ACCEPTED_FILE_TYPES,
      maxSize: MAX_FILE_SIZE,
      multiple: false,
      noClick: false,
      noKeyboard: false,
    });

  // Remove file handler
  const removeFile = useCallback(() => {
    if (currentFile?.preview) {
      URL.revokeObjectURL(currentFile.preview);
    }
    setCurrentFile(null);
    setError(null);
    setUploadProgress(0);
  }, [currentFile]);

  // Upload file handler
  const uploadFile = useCallback(async () => {
    if (!currentFile) return;

    setUploadProgress(0);
    setError(null);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', currentFile);

      // Show progress during upload
      setUploadProgress(35);

      // Use mutation hook which handles cache invalidation
      const response: OcrTaskStartResponse = await scanInvoice(formData);

      if (!response?.taskId) {
        throw new Error(
          'Respuesta inválida del servidor - ID de tarea faltante',
        );
      }

      const task = createProcessingTaskFromStartResponse(
        response,
        currentFile.name,
      );
      startTask(task);

      // Track this task so we can auto-navigate to the confirmation page
      // once the background processing completes.
      hasNavigatedRef.current = false;
      setPendingResultTaskId(response.taskId);

      toast.success('Factura enviada. Extrayendo datos…');
      removeFile();
    } catch (error: unknown) {
      if (isMountedRef.current) {
        setUploadProgress(0);
      }

      // Special handling for upload response errors
      if (
        error instanceof Error &&
        error.name === 'UploadResponseError' &&
        uploadProgress >= 100
      ) {
        if (isMountedRef.current) {
          // The upload likely succeeded but response parsing failed
          toast.error(
            'La factura se procesó pero hubo un error al mostrar los resultados. Revisa tu historial.',
          );
          setError('Upload completado pero error al procesar respuesta.');
          // Optionally redirect to dashboard or invoice list
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      } else {
        if (isMountedRef.current) {
          setError(getUploadErrorMessage(error));
          toast.error('Error al procesar la factura');
        }
      }
    } finally {
      if (isMountedRef.current) {
        setUploadProgress(0);
      }
    }
  }, [
    currentFile,
    navigate,
    removeFile,
    scanInvoice,
    startTask,
    uploadProgress,
  ]);

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  // Get file icon
  const getFileIcon = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      return <Icon name="image" size="sm" aria-hidden />;
    }
    if (file.type === 'application/pdf') {
      return <Icon name="file" size="sm" aria-hidden />;
    }
    return <Icon name="attachment" size="sm" aria-hidden />;
  }, []);

  // Get file type display name
  const getFileTypeName = useCallback((file: File): string => {
    if (file.type.startsWith('image/')) {
      return 'Imagen';
    }
    if (file.type === 'application/pdf') {
      return 'PDF';
    }
    return 'Archivo';
  }, []);

  return (
    <UploadContainer>
      <UploadTitle>
        <Icon name="file-upload" size="md" aria-hidden /> Escanear factura con
        IA
      </UploadTitle>
      <Card>
        <p
          className="section-description"
          style={{ textAlign: 'center', marginBottom: '1.5rem' }}
        >
          Sube una foto o PDF de tu factura de electricidad, agua o gas y la IA
          extraerá automáticamente los datos de consumo.
        </p>

        {/* Error message */}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        {/* Drop zone */}
        <DropZone
          {...getRootProps()}
          data-disabled={isMutationLoading}
          $isDragActive={isDragActive}
          $hasError={!!error}
          $isDragReject={isDragReject}
        >
          <input {...getInputProps()} />
          <UploadIcon>
            {isDragActive ? (
              <Icon name="download" size="xl" aria-hidden />
            ) : (
              <Icon name="file" size="xl" aria-hidden />
            )}
          </UploadIcon>
          <UploadText>
            <h3>
              {isDragActive
                ? '¡Suelta el archivo aquí!'
                : 'Arrastra tu factura aquí'}
            </h3>
            <p>o haz clic para seleccionar un archivo</p>
            <div className="file-requirements">
              Formatos soportados: JPG, PNG, PDF (máx. 5MB)
            </div>
          </UploadText>
        </DropZone>

        {/* File preview */}
        {currentFile && (
          <FilePreviewContainer>
            <FileIcon>{getFileIcon(currentFile)}</FileIcon>
            <FileInfo>
              <div className="file-name">{currentFile.name}</div>
              <div className="file-size">
                {formatFileSize(currentFile.size)}
              </div>
              <div className="file-type">{getFileTypeName(currentFile)}</div>
            </FileInfo>
            <RemoveButton
              variant="tertiary"
              onClick={removeFile}
              disabled={isMutationLoading}
              aria-label="Remover archivo"
            >
              <Icon name="close" size="xs" aria-hidden />
            </RemoveButton>
          </FilePreviewContainer>
        )}

        {/* Upload progress */}
        {isMutationLoading && (
          <ProgressBar
            progress={uploadProgress}
            text="Procesando factura..."
            variant="primary"
            size="md"
          />
        )}

        {/* Upload actions */}
        {currentFile && !isMutationLoading && (
          <UploadActions>
            <Button onClick={uploadFile} fullWidth>
              Procesar factura
            </Button>
          </UploadActions>
        )}

        <StatusList matchTask={invoiceTaskMatch} />
      </Card>
    </UploadContainer>
  );
};

InvoiceUpload.displayName = 'InvoiceUpload';
